import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import { useForm } from "react-hook-form";
import { Amplify } from 'aws-amplify';
import amplifyconfig from './amplifyconfiguration.json';
import { submitRegistration } from './api';
import LogoOnly from "./LogoOnly";
import ConfirmEmail from "./ConfirmEmail";
import Dashboard from "./Dashboard";
import CheckIn from "./CheckIn";
import { useLanguage } from "./i18n/LanguageContext";
import LanguageSelector from "./components/LanguageSelector";
import CookieConsent from "./components/CookieConsent";
import Footer from "./components/Footer";
import { COUNTRIES } from "./data/countries";
import { DIAL_CODES } from "./data/dialCodes";
import { mapUTMToCategory } from "./utils/utmCategoryMapping";
import { useSkin } from "./skins/useSkin";

const EVENT_NAME = process.env.REACT_APP_EVENT_NAME;
const UPDATE_POS = process.env.REACT_APP_UPDATE_POS;
const RESTRICT_EMAIL_DOMAINS = process.env.REACT_APP_RESTRICT_EMAIL_DOMAINS === 'true';

// Initialize Amplify - will be configured with actual values after deployment
Amplify.configure(amplifyconfig);

// ========================================
// TOGGLE THIS TO SHOW LOGO ONLY
// Set to true for clean white background with logo
// Set to false to show the full form
/// Should be true on initial prod launch
const SHOW_LOGO_ONLY = false;
// ========================================

// Check URL parameters before component mounts
const params = new URLSearchParams(window.location.search);
const onsite = params.get('onsite');
if (onsite === 'true' || onsite === 'false') {
  localStorage.setItem('onsite', onsite);
}

// Utility function to build Shop URL with UTM forwarding
const buildShopUrl = () => {
  const baseUrl = 'https://www.mytheresa.com/to/women/edits/the-maison-mytheresa-edit';
  const currentParams = new URLSearchParams(window.location.search);

  // Check if any UTM/campaign params exist in current URL
  const hasCampaignParams = currentParams.has('utm_source') ||
                            currentParams.has('utm_campaign') ||
                            currentParams.has('utm_medium');

  if (hasCampaignParams) {
    // Forward ALL incoming params to destination URL
    const targetUrl = new URL(baseUrl);
    for (const [key, value] of currentParams) {
      targetUrl.searchParams.set(key, value);
    }
    return targetUrl.toString();
  } else {
    // Use original default params when no campaign params present
    return `${baseUrl}?utm_source=yacht&utm_campaign=maison-yacht-2026`;
  }
};

// Utility function to get referral source with fallback hierarchy
const getReferralSource = () => {
  const urlParams = new URLSearchParams(window.location.search);

  // Capture click IDs (present regardless of UTM params)
  const gclid = urlParams.get('gclid') || '';
  const fbclid = urlParams.get('fbclid') || '';

  // Priority 1: Explicit UTM parameters
  if (urlParams.get('utm_source')) {
    const params = {
      source: urlParams.get('utm_source'),
      medium: urlParams.get('utm_medium') || '',
      campaign: urlParams.get('utm_campaign') || '',
    };
    return {
      ...params,
      method: 'utm_params',
      category: mapUTMToCategory(params, 'utm_params'),
      gclid,
      fbclid,
    };
  }

  // Priority 2: Parse referrer URL
  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      const hostname = referrerUrl.hostname;

      let params;
      // Map common referrers
      if (hostname.includes('facebook.com') || hostname.includes('fb.com')) {
        params = { source: 'facebook', medium: 'referral', campaign: '' };
      } else if (hostname.includes('google.')) {
        params = { source: 'google', medium: 'organic', campaign: '' };
      } else if (hostname.includes('instagram.com')) {
        params = { source: 'instagram', medium: 'referral', campaign: '' };
      } else {
        params = { source: hostname, medium: 'referral', campaign: '' };
      }
      return { ...params, method: 'referrer', category: mapUTMToCategory(params, 'referrer'), gclid, fbclid };
    } catch {
      // Invalid referrer URL, fall through to direct
    }
  }

  // Priority 3: Direct/unknown
  const params = { source: 'direct', medium: 'none', campaign: '' };
  return { ...params, method: 'unknown', category: mapUTMToCategory(params, 'unknown'), gclid, fbclid };
};

function App() {
  useSkin();
  const { t, translations, language } = useLanguage();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
    watch,
    trigger,
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const [submitted, setSubmitted] = useState(
    new URLSearchParams(window.location.search).get('preview') === 'success'
  );
  const [firstname, setFirstName] = useState("");
  const [emailProvidersVisible, setEmailProvidersVisible] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [isPendingDoi, setIsPendingDoi] = useState(false);
  const emailRef = useRef(null);

  // Check if user is onsite
  const isOnsite = localStorage.getItem("onsite") === "true";

  // Track page view on mount with referral data for channel attribution
  useEffect(() => {
    const trackPageView = async () => {
      try {
        // Get pageview API URL from amplify config
        const pageviewUrl = amplifyconfig.custom?.pageviewApiUrl;
        if (pageviewUrl) {
          // Include referral data for marketing channel tracking
          const referralData = getReferralSource();
          await fetch(pageviewUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pageId: 'registration',
              referral: {
                source: referralData.source,
                medium: referralData.medium,
                campaign: referralData.campaign,
                method: referralData.method,
                category: referralData.category,
              },
            }),
          });
        }
      } catch (error) {
        // Silently fail - page view tracking should not affect user experience
        console.debug('Page view tracking failed:', error);
      }
    };
    trackPageView();
  }, []);

  const emailProviders = [
    "@gmail.com",
    "@yahoo.com",
    "@icloud.com",
    "@me.com",
    "@hotmail.com",
    "@aol.com",
  ];

  // If SHOW_LOGO_ONLY is true, render only the logo
  if (SHOW_LOGO_ONLY) {
    return (
      <>
        <LogoOnly />
        <CookieConsent translations={translations} />
      </>
    );
  }

  const onSubmit = async (data) => {
    setFirstName(data.firstname.trim());
    data.event = EVENT_NAME;
    data.updatePOS = UPDATE_POS;
    data.onsite = isOnsite;
    data.language = language; // Pass user's language for confirmation email
    data.referral = getReferralSource(); // Add referral source tracking

    try {
      // Submit via Amplify API
      const response = await submitRegistration(data);

      if (response.ok) {
        // Backend returns status: PENDING_CONFIRMATION when a DOI email is
        // being sent (new DACH registrants); CONFIRMED / AUTO_CONFIRMED
        // otherwise. Drive the "check your email" copy off the real status
        // so already-opted-in DACH re-registrants see the standard thank-you.
        setConfirmationResult(null);
        setIsPendingDoi(response.data?.status === 'PENDING_CONFIRMATION');
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (isOnsite) {
          setTimeout(() => {
            setSubmitted(false);
            setConfirmationResult(null);
            setIsPendingDoi(false);
            reset();
          }, 10000);
        }
      } else {
        console.error("Failed to submit data to the prospects API");
      }
    } catch (error) {
      console.error("An error occurred", error);
    }
  };

  const handleBlur = (field) => {
    const value = document.getElementById(field).value.trim();
    setValue(field, value);
  };

  const formatPhoneNumber = (event) => {
    const cleaned = event.target.value.replace(/[^\d+\s-]/g, "").substring(0, 20);
    event.target.value = cleaned;
    setValue("phone", cleaned);
    if (errors.phoneCountry || cleaned === "") {
      trigger("phoneCountry");
    }
  };

  const handleFocus = (field) => {
    if (field === "email") {
      setEmailProvidersVisible(false);
    } else {
      setEmailProvidersVisible(false);
    }
  };

  const handleEmailInput = () => {
    setEmailProvidersVisible(false);
  };

  const handleEmailProviderClick = (provider) => {
    const emailInput = emailRef.current;
    const value = emailInput.value.trim();
    const atIndex = value.indexOf("@");
    const newValue =
      atIndex !== -1
        ? value.substring(0, atIndex) + provider
        : value + provider;
    emailInput.value = newValue;
    setValue("email", newValue);
    setEmailProvidersVisible(false);
  };

  // Helper function to render disclaimer with privacy policy link
  const renderDisclaimerText = () => {
    const disclaimerText = t('disclaimers.marketing');
    const privacyPolicyUrl = t('disclaimers.privacyPolicyUrl');
    const privacyPolicyText = t('disclaimers.privacyPolicyText');

    const parts = disclaimerText.split('{privacyPolicyLink}');

    return (
      <>
        {parts[0]}
        <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
          {privacyPolicyText}
        </a>
        {parts[1]}
      </>
    );
  };

  // Check if we're on the confirmation page
  const isConfirmPage = window.location.pathname === '/confirm' ||
                        window.location.search.includes('token=');

  // Check if we're on the dashboard page
  const isManagePage = window.location.pathname === '/manage';

  // Check if we're on the check-in page
  const isCheckinPage = window.location.pathname === '/checkin';

  // If on confirmation page, render ConfirmEmail component
  if (isConfirmPage) {
    return <ConfirmEmail />;
  }

  // If on manage page, render Dashboard component
  if (isManagePage) {
    return <Dashboard />;
  }

  // If on check-in page, render CheckIn component
  if (isCheckinPage) {
    return <CheckIn />;
  }

  // Show LogoOnly if SHOW_LOGO_ONLY is true
  if (SHOW_LOGO_ONLY) {
    return <LogoOnly />;
  }

  // Show the "check your email to confirm" copy only when the backend is
  // actually sending a DOI email (new DACH registrants). Already-opted-in
  // DACH re-registrants get the standard thank-you instead.
  const getSuccessMessage = () => {
    if (isPendingDoi) return t('success.dachNew');
    return t('success.nonDach');
  };

  if (submitted) {
    const successMsg = getSuccessMessage();

    return (
      <>
        <div className="language-selector-wrapper">
          <LanguageSelector />
        </div>
        <div className="content-container">
          <header className="App-header">
            <div className="logo-container">
              <img
                src="/images/svg/Maison_Mytheresa_Logo.svg"
                alt="Maison Mytheresa"
                className="logo-image"
              />
            </div>
            <img
              src="/images/svg/Line_object.svg"
              alt="Decorative line separator"
              className="separator-line"
            />
            <div className="success-message">
              <p>{successMsg}</p>
            </div>
            <img
              src="/images/svg/Line_object.svg"
              alt="Decorative line separator"
              className="separator-line"
            />
            <button
              type="button"
              onClick={() => window.open(buildShopUrl(), '_blank')}
              className="shop-now-button"
            >
              {t('form.shopNow')}
            </button>
            <div className="success-anchor-container">
              <img
                src="/images/anchor.png"
                alt=""
                aria-hidden="true"
                className="success-anchor-image"
              />
            </div>
          </header>
        </div>
        <Footer />
        <CookieConsent translations={translations} />
      </>
    );
  }

  return (
    <>
      <div className="language-selector-wrapper">
        <LanguageSelector />
      </div>
      <div className="content-container">
        <header className="App-header">
            <div className="logo-container">
              <img
                src="/images/svg/Maison_Mytheresa_Logo.svg"
                alt="Maison Mytheresa"
                className="logo-image"
              />
            </div>
            <img
              src="/images/svg/Line_object.svg"
              alt="Decorative line separator"
              className="separator-line"
            />
            {/* Only show event info if NOT onsite */}
            {!isOnsite && (
              <div className="event-info">
                {t('eventInfo.headline') && t('eventInfo.headline') !== 'eventInfo.headline' && (
                  <p className="headline-line">{t('eventInfo.headline')}</p>
                )}
                {t('eventInfo.dates') && <p className="dates-line">{t('eventInfo.dates')}</p>}
                {t('eventInfo.openingDays') && <p className="opening-days-line">{t('eventInfo.openingDays')}</p>}
                {t('eventInfo.hours') && <p className="hours-line">{t('eventInfo.hours')}</p>}
                {t('eventInfo.location') && <p className="location-line">{t('eventInfo.location')}</p>}
                {t('eventInfo.address') && <p className="address-line">{t('eventInfo.address')}</p>}
              </div>
            )}
            <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <input
              id="firstname"
              type="text"
              placeholder={t('form.firstName')}
              className={errors.firstname ? 'error' : ''}
              {...register("firstname", {
                required: true,
                pattern: /^[A-Za-z\s]+$/,
              })}
              onBlur={() => handleBlur("firstname")}
              onFocus={() => handleFocus("firstname")}
            />
          </div>

          <div className="form-group">
            <input
              id="lastname"
              type="text"
              placeholder={t('form.lastName')}
              className={errors.lastname ? 'error' : ''}
              {...register("lastname", {
                required: true,
                pattern: /^[A-Za-z\s]+$/,
              })}
              onBlur={() => handleBlur("lastname")}
              onFocus={() => handleFocus("lastname")}
            />
          </div>

          <div className="form-group">
            <input
              id="email"
              type="email"
              placeholder={t('form.email')}
              className={errors.email ? 'error' : ''}
              {...(() => {
                const reg = register("email", {
                  required: true,
                  pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/,
                  validate: (value) => {
                    if (!RESTRICT_EMAIL_DOMAINS) {
                      return true; // Allow all valid emails
                    }
                    const domain = value.toLowerCase().split('@')[1];
                    return domain === 'mytheresa.com' || domain === 'gmail.com' || 'Only @mytheresa.com or @gmail.com emails are allowed';
                  }
                });
                return {
                  ...reg,
                  ref: (el) => {
                    reg.ref(el);
                    emailRef.current = el;
                  },
                };
              })()}
              onBlur={() => handleBlur("email")}
              onFocus={() => handleFocus("email")}
              onInput={handleEmailInput}
            />
            {emailProvidersVisible && (
              <div className="email-providers">
                {emailProviders.map((provider) => (
                  <button
                    type="button"
                    key={provider}
                    onClick={() => handleEmailProviderClick(provider)}
                  >
                    {provider}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="form-group phone-group">
            <div className="phone-dial-code-wrapper">
              <select
                id="phoneCountry"
                aria-label={t('form.dialCode')}
                className={`phone-dial-code ${errors.phoneCountry ? 'error' : ''}`}
                {...register("phoneCountry", {
                  validate: (value, formValues) =>
                    !formValues.phone || !!value || 'dialCodeRequired',
                })}
                defaultValue=""
              >
                <option value="" disabled></option>
                {COUNTRIES.filter(c => DIAL_CODES[c.code]).map(country => (
                  <option key={country.code} value={country.code}>
                    {DIAL_CODES[country.code]} — {country.name}
                  </option>
                ))}
              </select>
              <span
                className={`phone-dial-code-display ${!watch("phoneCountry") ? 'is-placeholder' : ''}`}
                aria-hidden="true"
              >
                {DIAL_CODES[watch("phoneCountry")] || '+00'}
              </span>
            </div>
            <input
              id="phone"
              type="tel"
              placeholder={t('form.phone')}
              className={errors.phone ? 'error' : ''}
              {...register("phone")}
              onChange={formatPhoneNumber}
              onBlur={() => handleBlur("phone")}
              onFocus={() => handleFocus("phone")}
            />
          </div>

          <div className="form-group">
            <select
              id="country"
              className={errors.country ? 'error' : ''}
              {...register("country", { required: true })}
              defaultValue=""
            >
              <option value="" disabled>{t('form.country')}</option>
              {COUNTRIES.map(country => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group checkbox-group">
            <div className="disclaimer-container">
              <div className="disclaimer-text">
                <label className="disclaimer-label">
                  <input
                    type="checkbox"
                    className={errors.disclaimerChecked1 ? 'error' : ''}
                    {...register("disclaimerChecked1", { required: true })}
                  />
                  <span className="disclaimer-label-text">{renderDisclaimerText()}</span>
                </label>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="form-button"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              <span className="form-button-dots" aria-label={t('form.submitting') || 'Submitting'}>
                <span className="form-button-dot" />
                <span className="form-button-dot" />
                <span className="form-button-dot" />
              </span>
            ) : (
              t('form.submit')
            )}
          </button>
        </form>
        {/* Only show Shop Now button if NOT onsite */}
        {!isOnsite && (
          <button
            type="button"
            onClick={() => window.open(buildShopUrl(), '_blank')}
            className="shop-now-button"
          >
            {t('form.shopNow')}
          </button>
        )}
        {!isOnsite && (
          <div className="landing-anchor-container">
            <img
              src="/images/anchor.png"
              alt=""
              aria-hidden="true"
              className="landing-anchor-image"
            />
          </div>
        )}
          </header>
      </div>
            <Footer />
      <CookieConsent translations={translations} />
    </>
  );
}

export default App;
